// Melee combat profiles ported verbatim from the Unity project's
// MeleeCombatProfile assets (Assets/Crownfall/Combat Profiles/*.asset).
// All times in seconds, arcs in degrees, distances in meters.

export type ProfileId =
  | 'agent'
  | 'king'
  | 'royalGuard'
  | 'veteranSoldier'
  | 'trainingEnemy'
  | 'patrolGuard'
  | 'soldier';

export interface CombatProfile {
  maximumStamina: number;
  staminaRegenerationPerSecond: number;
  staminaRegenerationDelay: number;
  exhaustedRecoveryFraction: number;
  lightAttackDamage: number;
  lightAttackStaminaCost: number;
  lightAttackWindup: number;
  lightAttackRecovery: number;
  lightAttackRange: number;
  lightAttackRadius: number;
  lightAttackArc: number;
  blockArc: number;
  blockedDamageMultiplier: number;
  blockStaminaCostPerDamage: number;
  blockStaminaDrainPerSecond: number;
  sprintStaminaCostPerSecond: number;
  attackMoveMultiplier: number;
  blockMoveMultiplier: number;
  exhaustedMoveMultiplier: number;
  hitStunDuration: number;
  blockedHitStunDuration: number;
  guardBreakStunDuration: number;
}

const RAW: Record<ProfileId, CombatProfile> = {
  agent: {
    maximumStamina: 110,
    staminaRegenerationPerSecond: 30,
    staminaRegenerationDelay: 0.75,
    exhaustedRecoveryFraction: 0.38,
    lightAttackDamage: 34,
    lightAttackStaminaCost: 20,
    lightAttackWindup: 0.16,
    lightAttackRecovery: 0.28,
    lightAttackRange: 1.8,
    lightAttackRadius: 0.85,
    lightAttackArc: 110,
    blockArc: 135,
    blockedDamageMultiplier: 0.15,
    blockStaminaCostPerDamage: 0.55,
    blockStaminaDrainPerSecond: 6,
    sprintStaminaCostPerSecond: 17,
    attackMoveMultiplier: 0.5,
    blockMoveMultiplier: 0.52,
    exhaustedMoveMultiplier: 0.65,
    hitStunDuration: 0.24,
    blockedHitStunDuration: 0.08,
    guardBreakStunDuration: 0.65,
  },
  king: {
    maximumStamina: 75,
    staminaRegenerationPerSecond: 18,
    staminaRegenerationDelay: 1.1,
    exhaustedRecoveryFraction: 0.5,
    lightAttackDamage: 21,
    lightAttackStaminaCost: 28,
    lightAttackWindup: 0.28,
    lightAttackRecovery: 0.48,
    lightAttackRange: 1.55,
    lightAttackRadius: 0.75,
    lightAttackArc: 95,
    blockArc: 120,
    blockedDamageMultiplier: 0.35,
    blockStaminaCostPerDamage: 0.85,
    blockStaminaDrainPerSecond: 9,
    sprintStaminaCostPerSecond: 24,
    attackMoveMultiplier: 0.35,
    blockMoveMultiplier: 0.45,
    exhaustedMoveMultiplier: 0.58,
    hitStunDuration: 0.38,
    blockedHitStunDuration: 0.14,
    guardBreakStunDuration: 0.85,
  },
  royalGuard: {
    maximumStamina: 120,
    staminaRegenerationPerSecond: 26,
    staminaRegenerationDelay: 0.7,
    exhaustedRecoveryFraction: 0.36,
    lightAttackDamage: 27,
    lightAttackStaminaCost: 17,
    lightAttackWindup: 0.21,
    lightAttackRecovery: 0.34,
    lightAttackRange: 1.68,
    lightAttackRadius: 0.82,
    lightAttackArc: 112,
    blockArc: 145,
    blockedDamageMultiplier: 0.18,
    blockStaminaCostPerDamage: 0.5,
    blockStaminaDrainPerSecond: 4.5,
    sprintStaminaCostPerSecond: 18,
    attackMoveMultiplier: 0.5,
    blockMoveMultiplier: 0.58,
    exhaustedMoveMultiplier: 0.72,
    hitStunDuration: 0.22,
    blockedHitStunDuration: 0.07,
    guardBreakStunDuration: 0.58,
  },
  veteranSoldier: {
    maximumStamina: 105,
    staminaRegenerationPerSecond: 24,
    staminaRegenerationDelay: 0.78,
    exhaustedRecoveryFraction: 0.4,
    lightAttackDamage: 22,
    lightAttackStaminaCost: 17,
    lightAttackWindup: 0.24,
    lightAttackRecovery: 0.38,
    lightAttackRange: 1.6,
    lightAttackRadius: 0.78,
    lightAttackArc: 108,
    blockArc: 135,
    blockedDamageMultiplier: 0.25,
    blockStaminaCostPerDamage: 0.58,
    blockStaminaDrainPerSecond: 5,
    sprintStaminaCostPerSecond: 20,
    attackMoveMultiplier: 0.46,
    blockMoveMultiplier: 0.52,
    exhaustedMoveMultiplier: 0.68,
    hitStunDuration: 0.25,
    blockedHitStunDuration: 0.08,
    guardBreakStunDuration: 0.62,
  },
  trainingEnemy: {
    maximumStamina: 90,
    staminaRegenerationPerSecond: 20,
    staminaRegenerationDelay: 0.9,
    exhaustedRecoveryFraction: 0.45,
    lightAttackDamage: 18,
    lightAttackStaminaCost: 24,
    lightAttackWindup: 0.3,
    lightAttackRecovery: 0.5,
    lightAttackRange: 1.55,
    lightAttackRadius: 0.75,
    lightAttackArc: 100,
    blockArc: 125,
    blockedDamageMultiplier: 0.3,
    blockStaminaCostPerDamage: 0.75,
    blockStaminaDrainPerSecond: 7,
    sprintStaminaCostPerSecond: 20,
    attackMoveMultiplier: 0.4,
    blockMoveMultiplier: 0.45,
    exhaustedMoveMultiplier: 0.6,
    hitStunDuration: 0.32,
    blockedHitStunDuration: 0.12,
    guardBreakStunDuration: 0.75,
  },
  patrolGuard: {
    maximumStamina: 88,
    staminaRegenerationPerSecond: 20,
    staminaRegenerationDelay: 0.85,
    exhaustedRecoveryFraction: 0.42,
    lightAttackDamage: 19,
    lightAttackStaminaCost: 19,
    lightAttackWindup: 0.27,
    lightAttackRecovery: 0.42,
    lightAttackRange: 1.55,
    lightAttackRadius: 0.75,
    lightAttackArc: 105,
    blockArc: 130,
    blockedDamageMultiplier: 0.32,
    blockStaminaCostPerDamage: 0.7,
    blockStaminaDrainPerSecond: 6,
    sprintStaminaCostPerSecond: 20,
    attackMoveMultiplier: 0.44,
    blockMoveMultiplier: 0.5,
    exhaustedMoveMultiplier: 0.64,
    hitStunDuration: 0.28,
    blockedHitStunDuration: 0.1,
    guardBreakStunDuration: 0.68,
  },
  soldier: {
    maximumStamina: 72,
    staminaRegenerationPerSecond: 17,
    staminaRegenerationDelay: 0.95,
    exhaustedRecoveryFraction: 0.45,
    lightAttackDamage: 15,
    lightAttackStaminaCost: 18,
    lightAttackWindup: 0.3,
    lightAttackRecovery: 0.48,
    lightAttackRange: 1.5,
    lightAttackRadius: 0.72,
    lightAttackArc: 100,
    blockArc: 125,
    blockedDamageMultiplier: 0.4,
    blockStaminaCostPerDamage: 0.8,
    blockStaminaDrainPerSecond: 6,
    sprintStaminaCostPerSecond: 22,
    attackMoveMultiplier: 0.4,
    blockMoveMultiplier: 0.46,
    exhaustedMoveMultiplier: 0.6,
    hitStunDuration: 0.3,
    blockedHitStunDuration: 0.1,
    guardBreakStunDuration: 0.72,
  },
};

// Feel pass: the original numbers made stamina oppressive — you ran dry after
// a couple of exchanges and spent half the fight exhausted. Globally retune:
// much faster regen with a short flat delay, cheaper swings, near-free block
// upkeep, cheaper sprint, and quicker exhaustion recovery.
function tuned(p: CombatProfile): CombatProfile {
  return {
    ...p,
    staminaRegenerationPerSecond: p.staminaRegenerationPerSecond * 1.6,
    staminaRegenerationDelay: 0.5,
    exhaustedRecoveryFraction: 0.3,
    lightAttackStaminaCost: Math.round(p.lightAttackStaminaCost * 0.65),
    blockStaminaDrainPerSecond: 2,
    sprintStaminaCostPerSecond: p.sprintStaminaCostPerSecond * 0.5,
  };
}

export const PROFILES: Record<ProfileId, CombatProfile> = Object.fromEntries(
  (Object.entries(RAW) as [ProfileId, CombatProfile][]).map(([id, p]) => [id, tuned(p)])
) as Record<ProfileId, CombatProfile>;
